import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { TextField as MuiTextField, } from '@mui/material';
import { forwardRef, useEffect, useRef, useState, } from 'react';
import { skipProps, styled } from '../../utils';
import { InputAdornment } from '../InputAdornment';
import { CircularProgress } from '../Progress';
import { WithEndDecorator, } from '../WithEndDecorator';
export const TextField = forwardRef(function TextField(props, ref) {
    const { inputRef, InputProps, InputLabelProps, hasValue, onChange, helperText, endDecorator, emptyEndDecorator, startDecorator, emptyStartDecorator, warning = false, warningTimeout, // 10s
    onWarningExit, limit, label, required, loading, disabled, dataName, uppercase = false, ...otherProps } = props;
    // hold timeout in ref for warning timeout
    const warningTimeoutRef = useRef(null);
    // warning state
    const [warningState, setWarningState] = useState(false);
    const [fieldValue, setFieldValue] = useState(props.value);
    const handleChange = (event) => {
        const { value } = event.target;
        if (limit ? value.length <= limit : true) {
            const newValue = uppercase ? value.toUpperCase() : value;
            setFieldValue(newValue);
            if (onChange) {
                event['target'].value = newValue;
                onChange(event);
            }
        }
    };
    // effect for warning
    useEffect(() => {
        if (warning === true) {
            setWarningState(true);
            if (warningTimeoutRef.current && warningTimeout)
                clearTimeout(warningTimeoutRef.current);
            if (warningTimeout)
                warningTimeoutRef.current = window.setTimeout(() => {
                    setWarningState(false);
                    if (onWarningExit)
                        onWarningExit();
                }, warningTimeout);
        }
        else if (warning === false) {
            setWarningState(false);
        }
    }, [onWarningExit, warning, warningTimeout]);
    return (_jsx(WithEndDecorator, { emptyEndDecorator: emptyEndDecorator, endDecorator: endDecorator, startDecorator: startDecorator, emptyStartDecorator: emptyStartDecorator, decoratorComponent: _jsx(InternalTextFieldStyled, { "data-name": dataName ? dataName : '', inputRef: ref || inputRef, 
            // we overwrite the label to avoid the &thinsp; that is used for
            // the default mui implemtation of the required asterisk
            label: label && (_jsxs(_Fragment, { children: [label, required && '\xa0*'] })), ...otherProps, onChange: handleChange, hasValue: hasValue, InputLabelProps: InputLabelProps, disabled: disabled || loading, InputProps: loading
                ? {
                    ...InputProps,
                    endAdornment: (_jsx(InputAdornment, { position: "end", children: _jsx(CircularProgress, { filled: true, size: 20 }) })),
                }
                : InputProps, limit: limit, warningState: warningState, helperText: helperText, fieldValue: fieldValue }) }));
});
function InternalTextField({ className, fieldValue, ...props }) {
    return (_jsx(MuiTextField, { ...props, className: className, InputLabelProps: {
            classes: {
                root: `${className}-label`,
                shrink: `${className}-shrink`,
                animated: `${className}-animated`,
            },
            ...props.InputLabelProps,
        }, InputProps: {
            classes: {
                root: `${className}-input`,
                underline: `${className}-underline ${(!!props.inputProps?.value || props.hasValue || !!props.value) &&
                    !props.disabled
                    ? `${className}-inputActive`
                    : ''}`,
                disabled: `${className}-disabledUnderline`,
            },
            ...(props.limit
                ? {
                    inputProps: {
                        maxLength: props.limit,
                    },
                }
                : {}),
            ...props.InputProps,
        }, FormHelperTextProps: { className: `${className}-helperText` }, helperText: props.limit && props.limit > 0 ? (_jsxs("span", { className: `${className}-limitHelperText`, children: [_jsx("span", { children: props.helperText }), _jsxs("span", { children: [typeof fieldValue === 'string'
                            ? fieldValue.length ||
                                // fallback to prop value
                                (typeof props.value === 'string' ? props.value.length : 0)
                            : 0, "/", props.limit] })] })) : (props.helperText), variant: "standard" }));
}
const InternalTextFieldStyled = styled(InternalTextField, {
    shouldForwardProp: skipProps([
        'warningState',
        'hasValue',
    ]),
})(({ theme, warningState }) => ({
    '.MuiInputAdornment-positionEnd': {
        height: '100%',
        background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 15%, rgba(255,255,255,1) 100%)',
    },
    width: '100%',
    '&-label': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '100%',
        paddingRight: theme.spacing(3),
        '&:not(.Mui-error).Mui-focused': {
            color: theme.palette.primary.main,
        },
        '&.Mui-disabled': {
            color: theme.palette.white.dark,
        },
    },
    '&-shrink': {
        maxWidth: '133%',
    },
    '&-animated': {
        transition: theme.transitions.create(['color', 'transform', 'max-width'], {
            duration: theme.transitions.duration.shorter,
            easing: theme.transitions.easing.easeOut,
        }),
    },
    '&-input': {
        textOverflow: 'ellipsis',
        '&.Mui-disabled': {
            color: theme.palette.white.dark,
            '& .MuiIcon-root': {
                color: theme.palette.white.dark,
            },
        },
        '&.Mui-error': {
            color: theme.palette.error.main,
            '&:after': {
                borderBottomColor: theme.palette.error.main,
            },
            '& .MuiIcon-root': {
                color: theme.palette.error.main,
            },
        },
        '&.Mui-focused .MuiIcon-root': {
            color: theme.palette.primary.main,
        },
    },
    '&-underline': {
        '&:before': {
            borderBottomColor: warningState
                ? theme.palette.warning.main
                : theme.palette.primary.dark,
        },
        '&:after': {
            borderBottomColor: warningState
                ? theme.palette.warning.light
                : theme.palette.primary.light,
            borderBottomWidth: '1px',
        },
        '&:hover': {
            '&:not(.Mui-disabled):before': {
                borderBottomColor: warningState
                    ? theme.palette.warning.light
                    : theme.palette.primary.lighter,
            },
            '&:not(.Mui-disabled):after': {
                borderBottomWidth: '2px',
            },
            '&:not(.Mui-error):not(.Mui-disabled)': {
                '&:after': {
                    borderBottomColor: warningState
                        ? theme.palette.warning.light
                        : theme.palette.primary.lighter,
                },
                '& .MuiIcon-root': {
                    color: theme.palette.primary.lighter,
                },
            },
        },
    },
    '&-inputActive': {
        '&:not(.Mui-disabled):before': {
            borderBottomColor: warningState
                ? theme.palette.warning.main
                : theme.palette.primary.light,
        },
    },
    '&-disabledUnderline': {
        '&.Mui-disabled:before': {
            borderBottomStyle: 'solid',
            borderBottomColor: theme.palette.white.dark,
        },
    },
    '&-helperText': {
        color: warningState ? theme.palette.warning.main : 'inherit',
    },
    '&-limitHelperText': {
        display: 'flex',
        justifyContent: 'space-between',
    },
}));
